import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Crown, ExternalLink, Flag, Sparkles, Users } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import useSeo from '@/hooks/useSeo';
import { formatEthnicity } from '@/utils/ethnicity';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function resolveMedia(src) {
  if (!src) return null;
  if (src.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${src}`;
  return src;
}

export default function FoundingMembersPage() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useSeo({
    title: 'Founding Vendors · EntrepreNatives',
    description:
      'Meet the first 50 Founding Vendors of EntrepreNatives — the Indigenous entrepreneurs who believed in this platform early and helped take it global.',
    canonical: 'https://entreprenatives.com/founding-members',
    ogImage: 'https://entreprenatives.com/og-image.png',
  });

  useEffect(() => {
    (async () => {
      try {
        const [listRes, statusRes] = await Promise.all([
          fetch(`${API}/founding-vendors/list`),
          fetch(`${API}/founding-vendors/status`),
        ]);
        if (listRes.ok) setData(await listRes.json());
        if (statusRes.ok) setStatus(await statusRes.json());
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const founders = data?.vendors || [];
  const slotsRemaining = status?.slots_remaining ?? 50;
  const slotsTotal = status?.slots_total ?? 50;
  const slotsTaken = status?.slots_taken ?? 0;
  const pct = Math.min(100, (slotsTaken / slotsTotal) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0E0E23] via-[#1A1A3E] to-[#0E0E23] text-white" data-testid="founding-members-page">
      <Navbar />

      <main className="pt-24 pb-20">
        {/* Hero */}
        <section className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative overflow-hidden rounded-3xl border border-[#F59E0B]/30 p-8 sm:p-14 mb-12"
            style={{
              background:
                'linear-gradient(135deg, rgba(245,158,11,0.18) 0%, rgba(202,138,4,0.08) 45%, rgba(14,14,35,1) 100%)',
            }}
          >
            <div className="absolute -top-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-[#F59E0B]/15 blur-[120px]" />

            <div className="relative max-w-4xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F59E0B]/20 border border-[#F59E0B]/40 text-[#FCD34D] text-xs font-bold mb-5">
                <Crown size={12} /> THE FIFTY · OUR ORIGINAL FOUNDERS
              </div>
              <h1 className="font-syne text-4xl sm:text-6xl font-bold mb-5 leading-tight">
                These incredible individuals believed in <span className="text-[#FCD34D]">EntrepreNatives</span> and helped take this platform <span className="text-[#FCD34D]">global.</span>
              </h1>
              <p className="text-[#C4B5FD] font-outfit text-base sm:text-lg mb-4 leading-relaxed">
                We call them <strong className="text-white">The Fifty</strong> — the first 50 Indigenous entrepreneurs who crowdfunded EntrepreNatives from idea to reality.
                Every feature we ship, every vendor that joins after them, every buyer that discovers an Indigenous-owned business through us — it started here, with these founders.
              </p>
              <p className="text-[#9CA3AF] font-outfit text-sm sm:text-base mb-6 italic">
                Their names, stories, and businesses live on this page permanently — as long as EntrepreNatives exists.
              </p>

              {/* Slot meter */}
              <div className="max-w-lg mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-[#9CA3AF] font-semibold uppercase tracking-wide">
                    <span className="text-[#FCD34D] font-bold">{slotsTaken}</span> of {slotsTotal} seats claimed
                  </span>
                  <span className="text-xs text-[#FCD34D] font-mono" data-testid="founding-public-slot-count">
                    {slotsRemaining} still open
                  </span>
                </div>
                <div className="h-3 rounded-full bg-white/5 border border-white/10 overflow-hidden">
                  <motion.div
                    className="h-full"
                    style={{ background: 'linear-gradient(90deg, #F59E0B, #FCD34D)' }}
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.8 }}
                  />
                </div>
              </div>

              {slotsRemaining > 0 && (
                <Link
                  to="/dashboard/vendor/founding"
                  className="inline-flex items-center gap-3 px-6 py-3 rounded-xl text-[#0E0E23] font-bold text-sm shadow-[0_10px_40px_-10px_rgba(245,158,11,0.6)]"
                  style={{ background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)' }}
                  data-testid="founding-public-cta"
                >
                  <Crown size={16} /> Become a Founding Vendor
                </Link>
              )}
            </div>
          </motion.div>

          {/* Roll-call */}
          {loading ? (
            <div className="text-center text-[#9CA3AF] py-16">Loading founding vendors…</div>
          ) : founders.length === 0 ? (
            <>
              <div className="flex items-center gap-2 mb-6">
                <Users className="w-5 h-5 text-[#FCD34D]" />
                <h2 className="font-syne text-2xl font-bold">The Fifty — every seat is still open</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="founding-grid">
                {Array.from({ length: slotsTotal }).map((_, i) => (
                  <EmptySlot key={`empty-all-${i}`} number={i + 1} />
                ))}
              </div>
            </>
          ) : (
            <>
              {/* Featured #1 Founder - huge lavish card */}
              {founders[0] && <FeaturedFounderCard vendor={founders[0]} />}

              <div className="flex items-center gap-2 mb-6 mt-14">
                <Users className="w-5 h-5 text-[#FCD34D]" />
                <h2 className="font-syne text-2xl font-bold">
                  The Fifty — {founders.length} claimed, {slotsRemaining} waiting
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="founding-grid">
                {founders.slice(1).map((v, i) => (
                  <FounderCard key={v.vendor_id} vendor={v} index={i + 1} />
                ))}
                {/* Every unclaimed seat — not just 6 */}
                {Array.from({ length: slotsRemaining }).map((_, i) => (
                  <EmptySlot key={`empty-${i}`} number={slotsTaken + i + 1} />
                ))}
              </div>
            </>
          )}

          {/* Thanks */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 p-8 rounded-3xl bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 text-center"
          >
            <Sparkles className="w-8 h-8 text-[#A78BFA] mx-auto mb-3" />
            <h3 className="font-syne text-xl font-bold mb-2">Thank you, founders.</h3>
            <p className="text-[#C4B5FD] font-outfit max-w-xl mx-auto text-sm">
              Every feature we ship, every vendor that joins after you, every buyer that discovers
              an Indigenous-owned business through us — it started with you.
            </p>
          </motion.div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function FeaturedFounderCard({ vendor }) {
  const img = resolveMedia(vendor.profile_image) || resolveMedia(vendor.banner_image);
  return (
    <motion.div
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="relative mt-4"
      data-testid={`featured-founder-${vendor.vendor_id}`}
    >
      {/* Outer gold glow */}
      <div className="absolute -inset-0.5 rounded-3xl opacity-90 blur-[2px]" style={{ background: 'linear-gradient(135deg, #FCD34D, #F59E0B, #FCD34D)' }} />
      <div className="relative rounded-3xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a1406 0%, #2a1d06 55%, #0E0E23 100%)' }}>
        {/* Top ribbon */}
        <div className="absolute top-4 left-4 z-20 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[#0E0E23] text-[11px] font-extrabold tracking-wider shadow-lg"
          style={{ background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)' }}>
          <Crown size={12} /> FOUNDING MEMBER #{vendor.founding_vendor_number}
        </div>
        <div className="absolute top-4 right-4 z-20 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-[#FCD34D] text-[10px] font-mono tracking-wide border border-[#F59E0B]/40">
          THE ORIGINAL
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1.1fr,1.2fr] min-h-[360px]">
          {/* Image / cover */}
          <div className="relative aspect-square md:aspect-auto bg-[#1A1A3E] overflow-hidden">
            {img ? (
              <img src={img} alt={vendor.business_name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#8B5CF6]/20 to-[#F59E0B]/10">
                <Crown className="w-24 h-24 text-[#F59E0B]/40" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-tr from-[#0E0E23]/80 via-transparent to-transparent" />
          </div>

          {/* Body */}
          <div className="p-6 md:p-10 flex flex-col justify-center">
            <h2 className="font-syne text-3xl md:text-4xl font-bold text-white leading-tight mb-2" data-testid="featured-founder-name">
              {vendor.business_name}
            </h2>
            {vendor.owner_name && (
              <p className="text-[#FCD34D]/90 font-outfit font-semibold text-sm mb-4">by {vendor.owner_name}</p>
            )}
            <div className="flex flex-wrap gap-2 mb-5">
              {vendor.tribal_affiliation && (
                <span className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-[#FCD34D]/10 text-[#FCD34D] border border-[#FCD34D]/40 font-semibold">
                  <Flag size={10} /> {vendor.tribal_affiliation}
                </span>
              )}
              {formatEthnicity(vendor.ethnicity) && (
                <span className="text-xs px-3 py-1 rounded-full bg-[#8B5CF6]/15 text-[#C4B5FD] border border-[#8B5CF6]/30">
                  {formatEthnicity(vendor.ethnicity)}
                </span>
              )}
              {(vendor.service_city || vendor.service_state) && (
                <span className="text-xs px-3 py-1 rounded-full bg-white/5 text-[#9CA3AF] border border-white/10">
                  {[vendor.service_city, vendor.service_state].filter(Boolean).join(', ')}
                </span>
              )}
            </div>
            {vendor.founding_vendor_story ? (
              <p className="text-[#C4B5FD]/95 font-outfit leading-relaxed text-sm md:text-base mb-6 line-clamp-6">
                {vendor.founding_vendor_story}
              </p>
            ) : (
              <p className="italic text-[#9CA3AF] font-outfit text-sm mb-6">
                The very first entrepreneur to believe in this platform.
              </p>
            )}
            <Link
              to={`/vendor/${vendor.vendor_id}`}
              className="inline-flex items-center gap-2 self-start px-6 py-3 rounded-xl text-[#0E0E23] font-bold text-sm shadow-[0_10px_30px_-10px_rgba(245,158,11,0.6)]"
              style={{ background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)' }}
              data-testid="featured-founder-cta"
            >
              Visit business <ExternalLink size={14} />
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function FounderCard({ vendor, index }) {
  const img = resolveMedia(vendor.profile_image) || resolveMedia(vendor.banner_image);
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: (index % 6) * 0.05 }}
      whileHover={{ y: -4 }}
      className="group relative"
      data-testid={`founder-card-${vendor.vendor_id}`}
    >
      {/* Gold gradient border via pseudo-wrapper */}
      <div className="absolute -inset-[1px] rounded-2xl opacity-60 group-hover:opacity-100 transition blur-[1px]"
        style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.6), rgba(252,211,77,0.2), rgba(245,158,11,0.6))' }} />
      <div className="relative rounded-2xl overflow-hidden border border-[#F59E0B]/30 group-hover:border-[#F59E0B]/70 transition"
        style={{ background: 'linear-gradient(180deg, rgba(20,15,6,1) 0%, rgba(26,26,62,1) 100%)' }}>
        {/* Rank ribbon */}
        <div className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[#0E0E23] text-[10px] font-extrabold tracking-wider shadow"
          style={{ background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)' }}>
          <Crown size={10} /> #{vendor.founding_vendor_number}
        </div>

        {/* Image */}
        <div className="aspect-[4/3] w-full bg-[#1A1A3E] overflow-hidden relative">
          {img ? (
            <img src={img} alt={vendor.business_name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#8B5CF6]/20 to-[#F59E0B]/10">
              <Crown className="w-14 h-14 text-[#F59E0B]/50" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0E0E23] via-transparent to-transparent opacity-80" />
        </div>

        {/* Body */}
        <div className="p-5">
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <h3 className="font-syne text-base font-bold text-white leading-snug">{vendor.business_name}</h3>
            <Link
              to={`/vendor/${vendor.vendor_id}`}
              className="shrink-0 text-[#FCD34D] opacity-60 group-hover:opacity-100 hover:text-white transition"
              aria-label="View profile"
            >
              <ExternalLink size={15} />
            </Link>
          </div>
          {vendor.owner_name && (
            <p className="text-[11px] text-[#FCD34D]/80 font-outfit font-semibold mb-3">by {vendor.owner_name}</p>
          )}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {vendor.tribal_affiliation && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FCD34D]/10 text-[#FCD34D] border border-[#FCD34D]/30 flex items-center gap-1">
                <Flag size={9} /> {vendor.tribal_affiliation}
              </span>
            )}
            {(vendor.service_city || vendor.service_state) && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-[#9CA3AF] border border-white/10">
                {[vendor.service_city, vendor.service_state].filter(Boolean).join(', ')}
              </span>
            )}
          </div>
          {vendor.founding_vendor_story && (
            <p className="text-xs text-[#C4B5FD]/85 font-outfit leading-relaxed line-clamp-4 mb-4">
              {vendor.founding_vendor_story}
            </p>
          )}
          {/* Visit button */}
          <Link
            to={`/vendor/${vendor.vendor_id}`}
            className="flex items-center justify-center gap-1.5 py-2 rounded-lg text-[10px] font-bold tracking-wide uppercase border border-[#F59E0B]/30 text-[#FCD34D] hover:bg-[#F59E0B]/10 transition"
          >
            <Crown size={10} /> Visit business
          </Link>
        </div>
      </div>
    </motion.div>
  );
}

function EmptySlot({ number }) {
  return (
    <Link
      to="/dashboard/vendor/founding"
      className="group rounded-2xl border border-dashed border-[#F59E0B]/25 bg-white/[0.02] p-6 flex flex-col items-center justify-center min-h-[340px] text-center transition hover:bg-[#F59E0B]/[0.06] hover:border-[#F59E0B]/60"
      data-testid={`empty-founding-slot-${number}`}
    >
      <div className="w-14 h-14 rounded-full border-2 border-[#F59E0B]/30 flex items-center justify-center mb-4 group-hover:border-[#FCD34D] group-hover:bg-[#FCD34D]/10 transition">
        <Crown className="w-6 h-6 text-[#F59E0B]/60 group-hover:text-[#FCD34D] transition" />
      </div>
      <p className="text-[10px] text-[#9CA3AF] font-mono tracking-widest uppercase mb-2">Seat #{number} · Open</p>
      <p className="font-syne text-base font-bold text-white mb-1">Your spot is waiting</p>
      <p className="text-[#FCD34D] text-xs font-bold tracking-wide uppercase mb-4">Become a Founding Vendor</p>
      <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[11px] font-bold text-[#0E0E23] shadow group-hover:scale-105 transition"
        style={{ background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)' }}>
        <Crown size={11} /> Claim seat #{number}
      </span>
    </Link>
  );
}
